import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'
import App from '@/App'
import { LibraryRoute } from './LibraryRoute'
import { BACKEND_ENABLED } from '@/infrastructure/config/featureFlags'
import { RouteErrorBoundary } from '@/ui/error/RouteErrorBoundary'

// When the backend is disabled the login / auth-verify / status routes
// have no working surface (no API to hit). Each one is replaced with a
// silent redirect to the library so deep links and stale bookmarks
// land somewhere sensible instead of erroring or hammering an
// unreachable origin.
const redirectHome: RouteObject = {
  element: <Navigate to="/" replace />,
}
const backendRoutes: RouteObject[] = BACKEND_ENABLED
  ? [
      {
        path: 'login',
        lazy: async () => ({
          Component: (await import('./LoginRoute')).LoginRoute,
        }),
      },
      {
        path: 'auth/verify',
        lazy: async () => ({
          Component: (await import('./AuthVerifyRoute')).AuthVerifyRoute,
        }),
      },
      {
        path: 'status',
        lazy: async () => ({
          Component: (await import('./StatusRoute')).StatusRoute,
        }),
      },
    ]
  : [
      { path: 'login', ...redirectHome },
      { path: 'auth/verify', ...redirectHome },
      { path: 'status', ...redirectHome },
    ]

/**
 * The library route stays eager because it's the landing page —
 * splitting it would force a chunk fetch on first paint, hurting
 * LCP. Every other route lazy-loads via React Router's `lazy` prop;
 * Vite emits a separate chunk per dynamic import.
 *
 * Tech-spec §2.7: "The library route, the review route, and the
 * auth route are split into separate chunks via React.lazy."
 * Going further than the spec: the long-tail routes (history,
 * status, privacy, auth/verify) all split too — they're each a
 * one-shot navigation with no overlap with the steady-state UX.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    Component: App,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      { index: true, Component: LibraryRoute },
      {
        path: 'p/:projectId',
        lazy: async () => ({
          Component: (await import('./ProjectRoute')).ProjectRoute,
        }),
      },
      {
        path: 'p/:projectId/history',
        lazy: async () => ({
          Component: (await import('./VersionHistoryRoute')).VersionHistoryRoute,
        }),
      },
      ...backendRoutes,
      {
        path: 'privacy',
        lazy: async () => ({
          Component: (await import('./PrivacyRoute')).PrivacyRoute,
        }),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
